import { getSessionId } from '@/utils/session'

import { API_BASE } from './widget'

export type SafeFetchResponse<T> = [T, undefined] | [undefined, string]

export interface SafeFetchOptions extends RequestInit {
  uid?: string
  payload?: any
  queryParams?: Record<string, string | boolean>
  /** When true, POST/PUT/PATCH bodies are sent as-is without injecting `sessionId`. */
  skipSessionId?: boolean
  /**
   * When set, sends `Authorization: Bearer <token>` on the request. Used by
   * the reviews flow to attach the JWT issued by
   * `POST /widget/reviews/auth/verify/:uid/:pii`.
   */
  authToken?: string
}

/** Merge the session ID into the payload. */
function mergeSessionIntoPayload(payload: unknown): Record<string, unknown> {
  const sessionId = getSessionId()
  if (payload === undefined || payload === null) {
    return { sessionId }
  }
  if (typeof payload === 'object' && !Array.isArray(payload)) {
    return { ...(payload as Record<string, unknown>), sessionId }
  }
  return { sessionId, data: payload }
}

/** JSON `fetch` body string, or `undefined` when there is no payload. */
function getJsonBody(options: SafeFetchOptions): string | undefined {
  const method = (options.method || 'GET').toUpperCase()
  const sessionAware = (method === 'POST' || method === 'PUT' || method === 'PATCH') && !options.skipSessionId

  if (sessionAware) {
    return JSON.stringify(mergeSessionIntoPayload(options.payload))
  }
  if (options.payload !== undefined && options.payload !== null) {
    return JSON.stringify(options.payload)
  }
  return undefined
}

export async function safeFetchAPI<T = any>(
  url: string,
  options: SafeFetchOptions = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  }
): Promise<SafeFetchResponse<T>> {
  try {
    // Construct URL - if it starts with / or http, use as-is; otherwise prepend API_BASE
    const fullUrl = url.startsWith('http') || url.startsWith('/') ? url : `${API_BASE}/${url}`
    // Use window.location.origin as base to allow relative paths (like /proxy/...)
    const urlObj = new URL(fullUrl, window.location.origin)

    if (options.queryParams) {
      Object.entries(options.queryParams).forEach(([key, value]) => {
        if (value !== undefined) {
          urlObj.searchParams.append(key, value.toString())
        }
      })
    }

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(options.authToken ? { Authorization: `Bearer ${options.authToken}` } : {}),
      ...options.headers,
    }

    const config: RequestInit = {
      method: options.method || 'GET',
      headers,
      body: getJsonBody(options),
    }

    const response = await fetch(urlObj.toString(), config)

    // Check content type before parsing JSON
    const contentType = response.headers.get('content-type')
    if (!contentType || !contentType.includes('application/json')) {
      throw new Error(`Invalid response format: ${contentType || 'unknown'}`)
    }

    const data = await response.json()

    if (!response.ok || data.success === false) {
      const errorMessage = data.errors?.[0]?.message || data.message || response.statusText || 'Request failed'
      throw new Error(errorMessage)
    }

    // Handle AIQ response wrapper vs direct data
    return [data.data ?? data, undefined]
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Request failed'
    console.error('[API Error]', message)
    return [undefined, message]
  }
}
